import axios from 'axios';
import {
  SWR_CACHE_TTL_MS_DEFAULT,
  isApiGetEligibleForCache,
  buildCacheKey,
  readSwrCache,
  writeSwrCache,
} from './swrCache';
import { istNetzfehler, istDublette, reiheKommentarEin, sendeWarteschlange } from './offlineQueue'; // #322
import { istTerminRueckfrage, frageTerminAbsage, abbruchFehler } from './terminAbsage'; // #554

// Set axios baseURL to current origin to avoid <base> tag in email HTML changing relative API calls
try {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    axios.defaults.baseURL = window.location.origin;
  }
} catch (_) {}

// Helper to safely parse JSON
function safeParse(str) {
  try { return JSON.parse(str); } catch (_) { return null; }
}

// Helper: prüft, ob Auto-SSO erlaubt ist (Nutzer war schon einmal eingeloggt)
const hasWasLoggedInCookie = () => {
  try {
    if (typeof document === 'undefined') return false;
    const raw = String(document.cookie || '');
    return raw.split(';').some(c => c.trim().startsWith('was_logged_in=1'));
  } catch (_) {
    return false;
  }
};

// Lightweight in-memory SWR cache for GET /api/** requests.
// Die Cache-Entscheidung, der Schluessel und der gedeckelte Speicher liegen
// seit #309 in services/swrCache.js -- hier bleibt nur der Override.
const inflight = new Map(); // key -> Promise<AxiosResponse>

// Keep original axios.get
const _axiosGet = axios.get.bind(axios);

// Override axios.get to add SWR cache (for eligible) + request dedup (for all GET)
axios.get = async function(url, config = {}) {
  try {
    const eligible = isApiGetEligibleForCache(url, config);
    const ttlMs = typeof config.swrTTL === 'number' ? Math.max(0, config.swrTTL) : SWR_CACHE_TTL_MS_DEFAULT;
    const key = buildCacheKey(url, config);

    // Return fresh cache if eligible and still valid
    if (eligible) {
      const cached = readSwrCache(key);
      if (cached) {
        return Promise.resolve(cached);
      }
    }

    // Deduplicate inflight GET requests regardless of cache eligibility
    if (inflight.has(key)) {
      return inflight.get(key);
    }

    const p = _axiosGet(url, config).then((resp) => {
      try {
        if (eligible) {
          writeSwrCache(key, resp, ttlMs);
        }
      } catch (_) {}
      inflight.delete(key);
      return resp;
    }).catch((err) => {
      inflight.delete(key);
      throw err;
    });

    inflight.set(key, p);
    return p;
  } catch (e) {
    // Fallback to original get on any cache error
    return _axiosGet(url, config);
  }
};

// Token refresh tracking
let isRefreshingToken = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const apiService = {
  // Token refresh functionality with retry logic
  refreshToken: async () => {
    try {
      console.log('🔄 [TOKEN REFRESH] Attempting to refresh token...');
      
      // Track refresh attempts to prevent infinite loops
      const attemptKey = 'tokenRefreshAttempts';
      const attempts = parseInt(localStorage.getItem(attemptKey) || '0');
      
      if (attempts >= 3) {
        console.warn('🚨 [TOKEN REFRESH] Too many refresh attempts, forcing re-auth');
        localStorage.removeItem(attemptKey);
        throw new Error('Too many refresh attempts - automatic relogin required');
      }
      
      localStorage.setItem(attemptKey, (attempts + 1).toString());
      
      const response = await axios.post('/auth/refresh', {}, {
        // Skip interceptor for refresh request
        skipTokenRefresh: true,
        timeout: 10000 // 10 second timeout
      });
      
      if (response.data.success) {
        console.log('✅ [TOKEN REFRESH] Token refreshed successfully');
        console.log('🔄 [TOKEN REFRESH] Token expires on:', response.data.expiresOn);
        
        // Reset attempt counter on success
        localStorage.removeItem(attemptKey);
        
        // Broadcast success so Header/Home können sofort updaten
        try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('tokenRefreshSuccess')); } catch (_) {}
        
        return response.data;
      } else {
        throw new Error(response.data.error || 'Token refresh failed');
      }
    } catch (error) {
      console.error('❌ [TOKEN REFRESH] Token refresh failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        error: error.response?.data?.error,
        details: error.response?.data?.details,
        message: error.message
      });
      
      // If it's a 401, the session is completely expired
      if (error.response?.status === 401) {
        // Check for specific MSAL errors that indicate complete session expiry
        const errorDetails = error.response?.data?.details || '';
        if (errorDetails.includes('no_tokens_found') || errorDetails.includes('No refresh token found')) {
          throw new Error('Refresh token expired - automatic relogin required');
        }
        throw new Error('Session expired - login required');
      }
      
      throw error;
    }
  },

  // Generic GET method for debugging
  get: async (url, config = {}) => {
    return await axios.get(url, config);
  },

  // Authentication
  login: () => {
    // Interaktive Anmeldung immer erzwingen, wenn noch nie eingeloggt (kein Cookie)
    // und sicherstellen, dass der Server das als interaktiv erkennt
    window.location.href = hasWasLoggedInCookie() ? '/auth/sso' : '/auth/login?interactive=1';
  },

  logout: async () => {
    try {
      await axios.get('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  // Setup endpoints
  getSetupStatus: async () => {
    try {
      const response = await axios.get('/api/setup/status');
      return response.data;
    } catch (error) {
      console.error('Get setup status error:', error);
      throw error;
    }
  },

  configureSetup: async (setupData) => {
    try {
      console.log('🚀 API configureSetup called with:', setupData);
      const response = await axios.post('/api/setup/configure', setupData);
      console.log('🚀 API configureSetup response:', response);
      console.log('🚀 API configureSetup response.data:', response.data);
      return response.data;
    } catch (error) {
      console.error('🚀 Configure setup error:', error);
      console.error('🚀 Configure setup error response:', error.response?.data);
      throw error;
    }
  },

  // Admin - Mailbox monitoring
  getMailboxConfigs: async () => {
    try {
      const response = await axios.get('/api/admin/mailbox-configs');
      return response.data;
    } catch (error) {
      console.error('Get mailbox configs error:', error);
      throw error;
    }
  },

  createMailboxConfig: async (config) => {
    try {
      const response = await axios.post('/api/admin/mailbox-configs', config);
      return response.data;
    } catch (error) {
      console.error('Create mailbox config error:', error);
      throw error;
    }
  },

  updateMailboxConfig: async (id, updates) => {
    try {
      const response = await axios.patch(`/api/admin/mailbox-configs/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update mailbox config error:', error);
      throw error;
    }
  },

  deleteMailboxConfig: async (id) => {
    try {
      const response = await axios.delete(`/api/admin/mailbox-configs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete mailbox config error:', error);
      throw error;
    }
  },

  processMailboxNow: async (id) => {
    try {
      const response = await axios.post(`/api/admin/mailbox-configs/${id}/process-now`);
      return response.data;
    } catch (error) {
      console.error('Process mailbox now error:', error);
      throw error;
    }
  },

  // #649: Sperrliste für Auto-Antworten (Admin)
  getAutoReplyBlocklist: async () => {
    const response = await axios.get('/api/admin/auto-reply-blocklist', { params: { _: Date.now() } });
    return response.data;
  },

  saveAutoReplyBlocklist: async (eintraege) => {
    const response = await axios.put('/api/admin/auto-reply-blocklist', { eintraege });
    return response.data;
  },

  // Health check
  getHealth: async () => {
    try {
      const response = await axios.get('/api/health');
      return response.data;
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  },

  // User data
  getUser: async () => {
    try {
      const response = await axios.get('/api/user');
      return response.data;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await axios.get('/api/user');
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  // Email data (always live)
  getEmails: async () => {
    try {
      console.log('📧 [API SERVICE] Getting LIVE emails (last 7 days)...');
      // 30 s statt 10 s: Puffer für Postfächer mit hohem Volumen und langsame
      // Graph-Antworten. Der eigentliche Kostentreiber ist serverseitig behoben
      // ($select ohne body), das hier ist nur die Absicherung nach oben.
      const response = await axios.get('/api/emails', { timeout: 30000 });
      console.log('📧 [API SERVICE] LIVE emails response:', {
        count: response.data.value?.length || 0,
        live: response.data.live,
        cached: response.data.cached
      });
      return response.data;
    } catch (error) {
      console.error('Get LIVE emails error:', error);
      throw error;
    }
  },

  // Get full email details including body (optionally for delegated mailbox)
  getEmailDetails: async (emailId, mailboxAddress = undefined) => {
    try {
      const encodedEmailId = encodeURIComponent(emailId);
      const url = mailboxAddress
        ? `/api/emails/details/${encodedEmailId}?mailbox=${encodeURIComponent(mailboxAddress)}`
        : `/api/emails/details/${encodedEmailId}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Get email details error:', error);
      throw error;
    }
  },

  // Email push: open SSE stream
  subscribeEmailsPush: ({ onEvent, onOpen, onError } = {}) => {
    try {
      const url = '/api/emails/stream';
      const es = new EventSource(url);

      es.onopen = (ev) => {
        try { onOpen && onOpen(ev); } catch (_) {}
      };

      es.onerror = (err) => {
        try { onError && onError(err); } catch (_) {}
      };

      es.addEventListener('ready', (ev) => {
        try { onEvent && onEvent('ready', safeParse(ev.data)); } catch (_) {}
      });
      es.addEventListener('subscribed', (ev) => {
        try { onEvent && onEvent('subscribed', safeParse(ev.data)); } catch (_) {}
      });
      es.addEventListener('subscribe_error', (ev) => {
        try { onEvent && onEvent('subscribe_error', safeParse(ev.data)); } catch (_) {}
      });
      es.addEventListener('email_created', (ev) => {
        try { onEvent && onEvent('email_created', safeParse(ev.data)); } catch (_) {}
      });

      return es;
    } catch (e) {
      console.error('SSE subscribe error:', e);
      return null;
    }
  },

  // Full-text search across all emails (Outlook-like)
  searchEmails: async (query, limit = 200) => {
    try {
      console.log('📧 [API SERVICE] Searching emails...', { query, limit });
      const response = await axios.get('/api/emails/search', {
        params: { q: query, limit }
      });
      console.log('📧 [API SERVICE] Email search response:', {
        count: response.data.value?.length || 0,
        live: response.data.live,
        cached: response.data.cached
      });
      return response.data;
    } catch (error) {
      console.error('Email search error:', error);
      throw error;
    }
  },

  // Feature-Flags (F2/#35, Welle 2): welche Funktionen sind serverseitig aktiv?
  getFeatures: async () => {
    try {
      const response = await axios.get('/api/config/features');
      return response.data;
    } catch (error) {
      console.error('Features error:', error);
      throw error;
    }
  },

  // #520: alle Feature-Schalter gesammelt (Admin-Reiter „Features“). Ersetzt
  // im Client das frühere setCustomersEnabled; die Route customers-enabled bleibt.
  getAdminFeatures: async () => {
    try {
      const response = await axios.get(`/api/admin/features?_=${Date.now()}`, { disableSWR: true, swrTTL: 0 });
      return response.data;
    } catch (error) {
      console.error('Get admin features error:', error);
      throw error;
    }
  },

  updateAdminFeatures: async (aenderung) => {
    try {
      const response = await axios.patch('/api/admin/features', aenderung);
      return response.data;
    } catch (error) {
      console.error('Update admin features error:', error);
      throw error;
    }
  },

  // Global search across tickets/comments/cached email content/teams + user's mailbox
  globalSearch: async (query) => {
    try {
      // W0-5: ?limit/?mode wurden serverseitig nie ausgewertet — entfernt
      const response = await axios.get('/api/search/global', { params: { q: query } });
      return response.data;
    } catch (error) {
      console.error('Global search error:', error);
      throw error;
    }
  },

  aiFollowup: async ({ followup, originalQuestion, contextText, previousAnswer }) => {
    try {
      const response = await axios.post('/api/search/ai-followup', {
        followup,
        originalQuestion,
        contextText,
        previousAnswer
      });
      return response.data;
    } catch (error) {
      console.error('AI followup error:', error);
      throw error;
    }
  },

  // #122: Gespeicherte KI-Suche-Verläufe (benutzergebunden)
  listSearchConversations: async () => {
    const response = await axios.get('/api/search/conversations', { params: { _: Date.now() } });
    return response.data;
  },

  getSearchConversation: async (id) => {
    const response = await axios.get(`/api/search/conversations/${id}`);
    return response.data;
  },

  createSearchConversation: async ({ title, messages, contextText }) => {
    const response = await axios.post('/api/search/conversations', { title, messages, contextText });
    return response.data;
  },

  updateSearchConversation: async (id, { messages }) => {
    const response = await axios.patch(`/api/search/conversations/${id}`, { messages });
    return response.data;
  },

  deleteSearchConversation: async (id) => {
    const response = await axios.delete(`/api/search/conversations/${id}`);
    return response.data;
  },

  // #512/#513: KayCee-Assistent. Den Verlauf hält der Server je Benutzer,
  // der Client schickt nur die neue Frage.
  assistentStand: async () => {
    const response = await axios.get('/api/assistent', { params: { _: Date.now() } });
    return response.data;
  },

  assistentNachricht: async (text) => {
    const response = await axios.post('/api/assistent/nachricht', { text });
    return response.data;
  },

  assistentZusammenfassen: async () => {
    const response = await axios.post('/api/assistent/zusammenfassen');
    return response.data;
  },

  assistentLeeren: async () => {
    const response = await axios.delete('/api/assistent');
    return response.data;
  },

  assistentSpeichern: async (title) => {
    const response = await axios.post('/api/assistent/speichern', title ? { title } : {});
    return response.data;
  },

  assistentOeffnen: async (id) => {
    const response = await axios.post(`/api/assistent/laden/${id}`);
    return response.data;
  },

  // #123: „Ähnliche Vorfälle" aus dem Ticket heraus (KI-Suche mit eigenem Prompt)
  ticketAiSimilar: async (ticketId) => {
    const response = await axios.get(`/api/tickets/${ticketId}/ai-similar`);
    return response.data;
  },

  // Mark email as read/unread
  markEmailAsRead: async (emailId, isRead, mailboxAddress = undefined) => {
    try {
      const encodedEmailId = encodeURIComponent(emailId);
      const payload = mailboxAddress ? { isRead, mailboxAddress } : { isRead };
      const response = await axios.patch(`/api/emails/${encodedEmailId}/read`, payload);
      return response.data;
    } catch (error) {
      console.error('Mark email as read error:', error);
      throw error;
    }
  },

  // Calendar data (always live, ±1 month)
  getEvents: async (startDate = null, endDate = null) => {
    try {
      console.log('📅 [API SERVICE] Getting LIVE events (±1 month)...', {
        startDate: startDate ? startDate.toISOString() : 'none',
        endDate: endDate ? endDate.toISOString() : 'none'
      });
      
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      
      const url = `/api/events${params.toString() ? '?' + params.toString() : ''}`;
      console.log('📅 [API SERVICE] Request URL:', url);
      
      const response = await axios.get(url, { timeout: 10000 });
      console.log('📅 [API SERVICE] LIVE events response:', {
        count: response.data.value?.length || 0,
        live: response.data.live,
        cached: response.data.cached
      });
      return response.data;
    } catch (error) {
      console.error('📅 [API SERVICE] Get LIVE events error:', error);
      console.error('📅 [API SERVICE] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Global Calendar Events for multiple users
  getGlobalEvents: async (userEmails, startDate = null, endDate = null) => {
    try {
      console.log('🌍 [API SERVICE] Getting LIVE global events for multiple users...', {
        userEmails,
        startDate: startDate ? startDate.toISOString() : 'none',
        endDate: endDate ? endDate.toISOString() : 'none'
      });
      
      const params = new URLSearchParams();
      params.append('userEmails', userEmails.join(','));
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      
      const url = `/api/global-events?${params.toString()}`;
      console.log('🌍 [API SERVICE] Request URL:', url);
      
      const response = await axios.get(url);
      console.log('🌍 [API SERVICE] LIVE global events response:', {
        totalEvents: response.data.totalEvents,
        userCount: response.data.userCount,
        live: response.data.live
      });
      return response.data;
    } catch (error) {
      console.error('🌍 [API SERVICE] Get LIVE global events error:', error);
      console.error('🌍 [API SERVICE] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Ticket data
  getTickets: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      // Add cache-buster to coalesce duplicate rapid calls server-side response cache (3s)
      // but avoid busting within a short window
      const qs = params.toString();
      const url = qs ? `/api/tickets?${qs}` : '/api/tickets';
      const response = await axios.get(url, { swrTTL: 2500 });
      return response.data;
    } catch (error) {
      console.error('Get tickets error:', error);
      throw error;
    }
  },

  // #650: Zuletzt abgeschlossen (14 Tage, höchstens 50), scope 'mine' | 'all'.
  // Ohne SWR: Nach einem Abschluss soll die Liste den neuen Stand zeigen.
  getRecentlyClosedTickets: async (scope = 'mine') => {
    const response = await axios.get('/api/tickets/recently-closed', { params: { scope, _: Date.now() }, disableSWR: true, swrTTL: 0 });
    return response.data;
  },

  // Templates
  getTemplates: async () => {
    try {
      const ts = Date.now();
      const response = await axios.get(`/api/templates?_=${ts}`, { disableSWR: true, swrTTL: 0 });
      return response.data;
    } catch (error) {
      console.error('Get templates error:', error);
      throw error;
    }
  },
  createTemplate: async (payload) => {
    try {
      const response = await axios.post('/api/templates', payload);
      return response.data;
    } catch (error) {
      console.error('Create template error:', error);
      throw error;
    }
  },
  updateTemplate: async (id, payload) => {
    try {
      const response = await axios.patch(`/api/templates/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Update template error:', error);
      throw error;
    }
  },
  skipTemplateTurnusOnce: async (id) => {
    try {
      const response = await axios.post(`/api/templates/${id}/skip-turnus-once`);
      return response.data;
    } catch (error) {
      console.error('Skip template turnus once error:', error);
      throw error;
    }
  },
  deleteTemplate: async (id) => {
    try {
      const response = await axios.delete(`/api/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete template error:', error);
      throw error;
    }
  },
  restoreTemplate: async (templatePayload) => {
    try {
      const response = await axios.post('/api/templates/restore', { template: templatePayload });
      return response.data;
    } catch (error) {
      console.error('Restore template error:', error);
      throw error;
    }
  },

  // Projects
  getProjects: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && String(filters[key]).length > 0) {
          params.append(key, filters[key]);
        }
      });
      const response = await axios.get(`/api/projects${params.toString() ? '?' + params.toString() : ''}`);
      return response.data;
    } catch (error) {
      console.error('Get projects error:', error);
      throw error;
    }
  },

  // #602: Ein Vorgang bzw. Teilvorgang für den Bearbeiten-Dialog, auch
  // automatisch angelegte (die Liste blendet sie aus). Mit `darfBearbeiten`.
  getProject: async (projectId) => {
    const response = await axios.get(`/api/projects/${encodeURIComponent(projectId)}`);
    return response.data;
  },
  getSubproject: async (subprojectId) => {
    const response = await axios.get(`/api/subprojects/${encodeURIComponent(subprojectId)}`);
    return response.data;
  },

  // Aggregierte Projekt-Daten
  getProjectHistory: async (projectId) => {
    try {
      const response = await axios.get(`/api/projects/${projectId}/history`);
      return response.data;
    } catch (error) {
      console.error('Get project history error:', error);
      throw error;
    }
  },
  getProjectEmails: async (projectId) => {
    try {
      const response = await axios.get(`/api/projects/${projectId}/emails`);
      return response.data;
    } catch (error) {
      console.error('Get project emails error:', error);
      throw error;
    }
  },
  getProjectAttachments: async (projectId) => {
    try {
      const response = await axios.get(`/api/projects/${projectId}/attachments`);
      return response.data;
    } catch (error) {
      console.error('Get project attachments error:', error);
      throw error;
    }
  },
  getProjectTeamsMessages: async (projectId) => {
    try {
      const response = await axios.get(`/api/projects/${projectId}/teams-messages`);
      return response.data;
    } catch (error) {
      console.error('Get project Teams messages error:', error);
      throw error;
    }
  },

  // Subprojects
  getSubprojects: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && String(filters[key]).length > 0) {
          params.append(key, filters[key]);
        }
      });
      const response = await axios.get(`/api/subprojects${params.toString() ? '?' + params.toString() : ''}`);
      return response.data;
    } catch (error) {
      console.error('Get subprojects error:', error);
      throw error;
    }
  },

  // Aggregierte Unterprojekt-Daten
  getSubprojectHistory: async (subprojectId) => {
    try {
      const response = await axios.get(`/api/subprojects/${subprojectId}/history`);
      return response.data;
    } catch (error) {
      console.error('Get subproject history error:', error);
      throw error;
    }
  },
  getSubprojectEmails: async (subprojectId) => {
    try {
      const response = await axios.get(`/api/subprojects/${subprojectId}/emails`);
      return response.data;
    } catch (error) {
      console.error('Get subproject emails error:', error);
      throw error;
    }
  },
  getSubprojectTeamsMessages: async (subprojectId) => {
    try {
      const response = await axios.get(`/api/subprojects/${subprojectId}/teams-messages`);
      return response.data;
    } catch (error) {
      console.error('Get subproject Teams messages error:', error);
      throw error;
    }
  },
  getSubprojectAttachments: async (subprojectId) => {
    try {
      const response = await axios.get(`/api/subprojects/${subprojectId}/attachments`);
      return response.data;
    } catch (error) {
      console.error('Get subproject attachments error:', error);
      throw error;
    }
  },

  createProject: async (projectData) => {
    try {
      const response = await axios.post('/api/projects', projectData);
      return response.data;
    } catch (error) {
      console.error('Create project error:', error);
      throw error;
    }
  },

  updateProject: async (projectId, updates) => {
    try {
      const response = await axios.patch(`/api/projects/${projectId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update project error:', error);
      throw error;
    }
  },

  addSubprojectsToProject: async (projectId, subprojects) => {
    try {
      const response = await axios.post(`/api/projects/${projectId}/subprojects`, { subprojects });
      return response.data;
    } catch (error) {
      console.error('Add subprojects error:', error);
      throw error;
    }
  },

  updateSubproject: async (subprojectId, updates) => {
    try {
      const response = await axios.patch(`/api/subprojects/${subprojectId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update subproject error:', error);
      throw error;
    }
  },

  createProjectTimeblocks: async (projectId, blocks) => {
    try {
      const response = await axios.post(`/api/projects/${projectId}/timeblocks`, { blocks });
      return response.data;
    } catch (error) {
      console.error('Create project timeblocks error:', error);
      throw error;
    }
  },

  createSubprojectTimeblocks: async (subprojectId, blocks) => {
    try {
      const response = await axios.post(`/api/subprojects/${subprojectId}/timeblocks`, { blocks });
      return response.data;
    } catch (error) {
      console.error('Create subproject timeblocks error:', error);
      throw error;
    }
  },

  getTicket: async (id) => {
    try {
      const response = await axios.get(`/api/tickets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get ticket error:', error);
      throw error;
    }
  },

  getOpenTickets: async () => {
    try {
      const response = await axios.get('/api/tickets/open');
      return response.data;
    } catch (error) {
      console.error('Get open tickets error:', error);
      throw error;
    }
  },

  getAvailableEmails: async () => {
    try {
      const response = await axios.get('/api/tickets/available-emails');
      return response.data;
    } catch (error) {
      console.error('Get available emails error:', error);
      throw error;
    }
  },

  // Noch ohne Aufrufer, bleibt bewusst stehen: Auswertung ist Konzept #449 (#419).
  getTicketStatistics: async () => {
    try {
      const response = await axios.get('/api/tickets/statistics');
      return response.data;
    } catch (error) {
      console.error('Get ticket statistics error:', error);
      throw error;
    }
  },

  createTicket: async (ticketData) => {
    try {
      const response = await axios.post('/api/tickets', ticketData);
      return response.data;
    } catch (error) {
      console.error('Create ticket error:', error);
      throw error;
    }
  },

  getPlannedTickets: async (startDate, endDate) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      
      const response = await axios.get(`/api/tickets/planned?${params}`);
      return response.data;
    } catch (error) {
      console.error('Get planned tickets error:', error);
      throw error;
    }
  },

  planTicket: async (ticketId, planningData) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/plan`, planningData);
      return response.data;
    } catch (error) {
      console.error('Plan ticket error:', error);
      throw error;
    }
  },

  // #119 (W7): erster freier Arbeitszeit-Slot der nächsten Kalenderwoche
  planTicketNextWeek: async (ticketId) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/plan-next-week`);
      return response.data;
    } catch (error) {
      console.error('Plan ticket next week error:', error);
      throw error;
    }
  },

  planTicketForUser: async (ticketId, planningData, targetUserEmail) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/plan-for-user`, {
        ...planningData,
        targetUserEmail
      });
      return response.data;
    } catch (error) {
      console.error('Plan ticket for user error:', error);
      throw error;
    }
  },

  updateTicketPlanning: async (ticketId, planningData) => {
    try {
      const response = await axios.put(`/api/tickets/${ticketId}/planning`, planningData);
      return response.data;
    } catch (error) {
      console.error('Update ticket planning error:', error);
      throw error;
    }
  },

  unplanTicket: async (ticketId) => {
    try {
      const response = await axios.delete(`/api/tickets/${ticketId}/planning`);
      return response.data;
    } catch (error) {
      console.error('Unplan ticket error:', error);
      throw error;
    }
  },

  // #208: Beschreibung per LLM zusammenfassen (Schritt 1: Vorschlag, speichert nichts)
  summarizeTicketDescription: async (ticketId) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/ai-summarize-description`);
      return response.data;
    } catch (error) {
      console.error('Summarize ticket description error:', error);
      throw error;
    }
  },

  // #208 Schritt 2: bestätigten Vorschlag übernehmen (Original wird serverseitig
  // als interner Kommentar gesichert)
  applySummarizedDescription: async (ticketId, description) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/apply-summarized-description`, { description });
      return response.data;
    } catch (error) {
      console.error('Apply summarized description error:', error);
      throw error;
    }
  },

  createTicketFromEmail: async (emailId, ticketData, emailData = null) => {
    try {
      console.log(`🎫 [API SERVICE] Creating ticket from email ${emailId} (with cache data: ${!!emailData})`);
      
      const response = await axios.post('/api/tickets/from-email', {
        emailId,
        emailData, // Cache E-Mail-Daten wenn vorhanden
        ticketData // Ticket-Daten als separates Objekt
      });
      
      console.log(`🎫 [API SERVICE] Ticket created from email (cached: ${response.data.cached})`);
      
      return response.data;
    } catch (error) {
      console.error('Create ticket from email error:', error);
      throw error;
    }
  },

  updateTicket: async (id, ticketData) => {
    try {
      const response = await axios.put(`/api/tickets/${id}`, ticketData);
      return response.data;
    } catch (error) {
      // #554: Termin mit Teilnehmern -- erst fragen, dann bestaetigt wiederholen
      if (istTerminRueckfrage(error) && !ticketData?.terminAbsageBestaetigt) {
        const ja = await frageTerminAbsage(error.response.data.termine);
        if (ja) return apiService.updateTicket(id, { ...ticketData, terminAbsageBestaetigt: true });
        throw abbruchFehler();
      }
      console.error('Update ticket error:', error);
      throw error;
    }
  },

  getTicketClosingInfo: async (ticketId) => {
    try {
      const response = await axios.get(`/api/tickets/${ticketId}/closing-info`);
      return response.data;
    } catch (error) {
      console.error('Get ticket closing info error:', error);
      throw error;
    }
  },

  // Archive a project if it has no active tickets left
  archiveProjectIfEmpty: async (projectId) => {
    try {
      const response = await axios.post(`/api/projects/${projectId}/archive-if-empty`);
      return response.data;
    } catch (error) {
      console.error('Archive project if empty error:', error);
      throw error;
    }
  },

  hasActiveTicketsForProject: async (projectId) => {
    try {
      const response = await axios.get(`/api/projects/${projectId}/has-active-tickets`);
      return response.data;
    } catch (error) {
      console.error('Check active tickets for project error:', error);
      throw error;
    }
  },

  

  deleteProject: async (projectId) => {
    try {
      const response = await axios.delete(`/api/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Delete project error:', error);
      throw error;
    }
  },

  deleteSubproject: async (subprojectId) => {
    try {
      const response = await axios.delete(`/api/subprojects/${subprojectId}`);
      return response.data;
    } catch (error) {
      console.error('Delete subproject error:', error);
      throw error;
    }
  },

  // Create email reply draft
  createEmailReply: async (emailId, replyData = {}) => {
    try {
      console.log(`📧 [API SERVICE] Creating reply draft for email ${emailId}...`);
      const encodedEmailId = encodeURIComponent(emailId);
      const response = await axios.post(`/api/emails/${encodedEmailId}/reply`, replyData);
      
      console.log(`📧 [API SERVICE] Reply draft created successfully`);
      
      const result = response.data || {};
      if (response.data?.success) {
        result.sendFromFallback = !!response.data.sendFromFallback;
        result.mailboxContext = response.data.mailboxContext || null;
      }
      return result;
    } catch (error) {
      console.error('Create email reply error:', error);
      // Include fallback flag from server error if present
      if (error.response?.data) {
        error.response.data.sendFromFallback = !!error.response.data.sendFromFallback;
        error.response.data.mailboxContext = error.response.data.mailboxContext || null;
      }
      throw error;
    }
  },

  // Ticket-Subject-Prefix der Installation (z.B. "KC" für [KC#...])
  // Cached, damit wir nicht bei jedem Compose den Endpoint erneut anfragen.
  _ticketPrefixCache: null,
  getTicketPrefix: async function () {
    if (this._ticketPrefixCache) return this._ticketPrefixCache;
    try {
      const response = await axios.get('/api/config/ticket-prefix');
      const data = response.data || {};
      this._ticketPrefixCache = {
        prefix: data.prefix || 'KC',
        tagExample: data.tagExample || null,
        acceptLegacy: !!data.acceptLegacy
      };
      return this._ticketPrefixCache;
    } catch (error) {
      console.error('getTicketPrefix error, falling back to "KC":', error);
      return { prefix: 'KC', tagExample: null, acceptLegacy: false };
    }
  },
  buildTicketSubjectTag: async function (ticketNumber) {
    const { prefix } = await this.getTicketPrefix();
    const num = String(ticketNumber || '').trim();
    return num ? `[${prefix}#${num}]` : '';
  },

  // Create new email conversation draft for a ticket
  createNewEmailConversation: async (conversationData = {}) => {
    try {
      console.log(`📧 [API SERVICE] Creating new email conversation...`);
      
      const response = await axios.post('/api/emails/new-conversation', conversationData);
      
      console.log(`📧 [API SERVICE] New email conversation draft created successfully`);
      
      return response.data;
    } catch (error) {
      console.error('Create new email conversation error:', error);
      throw error;
    }
  },

  addEmailToTicket: async (ticketId, emailId, notes = null, emailData = null) => {
    try {
      console.log(`📧 [API SERVICE] Adding email ${emailId} to ticket ${ticketId} (with cache data: ${!!emailData})`);
      
      const response = await axios.post(`/api/tickets/${ticketId}/emails`, {
        emailId,
        notes,
        emailData // Cache E-Mail-Daten wenn vorhanden
      });
      
      console.log(`📧 [API SERVICE] Email added to ticket (cached: ${response.data.cached})`);
      
      return response.data;
    } catch (error) {
      console.error('Add email to ticket error:', error);
      throw error;
    }
  },

  // #447: Knopf „Mail vom Ticket lösen“ im Ticket-Verlauf. 404 = nicht (mehr) verknüpft,
  // 409 = automatisch zugeordnet (lässt sich nicht lösen).
  removeEmailFromTicket: async (ticketId, emailId) => {
    try {
      console.log(`📧 [API SERVICE] Removing email ${emailId} from ticket ${ticketId}`);
      const encodedEmailId = encodeURIComponent(emailId);
      const response = await axios.delete(`/api/tickets/${ticketId}/emails/${encodedEmailId}`);
      
      console.log(`📧 [API SERVICE] Email removed from ticket successfully`);
      
      return response.data;
    } catch (error) {
      console.error('Remove email from ticket error:', error);
      throw error;
    }
  },

  // Batch automatic email assignment
  // #436: Einträge sind { id, mailboxAddress } (oder, wie früher, nur die ID).
  batchAutoAssignEmails: async (eintraege) => {
    try {
      const response = await axios.post('/api/emails/batch-auto-assign', {
        emailIds: eintraege
      });
      return response.data;
    } catch (error) {
      console.error('Batch auto-assign emails error:', error);
      throw error;
    }
  },

  // E-Mail-Anhänge laden
  getEmailAttachments: async (emailId, mailboxAddress = undefined) => {
    try {
      console.log(`📎 [API SERVICE] Loading attachments for email ${emailId}`);
      
      // URL-encode the email ID to handle special characters
      const encodedEmailId = encodeURIComponent(emailId);
      const url = mailboxAddress 
        ? `/api/emails/${encodedEmailId}/attachments?mailbox=${encodeURIComponent(mailboxAddress)}`
        : `/api/emails/${encodedEmailId}/attachments`;
      const response = await axios.get(url);
      
      console.log(`📎 [API SERVICE] Found ${response.data.attachments?.length || 0} attachments`);
      
      return response.data;
    } catch (error) {
      console.error('Get email attachments error:', error);
      throw error;
    }
  },

  deleteTicket: async (id) => {
    try {
      const response = await axios.delete(`/api/tickets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete ticket error:', error);
      throw error;
    }
  },

  // Merge source ticket into target ticket
  mergeTickets: async (sourceTicketId, targetTicketId) => {
    try {
      const response = await axios.post('/api/tickets/merge', { sourceTicketId, targetTicketId });
      return response.data;
    } catch (error) {
      console.error('Merge tickets error:', error);
      throw error;
    }
  },

  // Ticket history and comments
  getTicketHistory: async (ticketId) => {
    try {
      const response = await axios.get(`/api/tickets/${ticketId}/history`);
      return response.data;
    } catch (error) {
      console.error('Get ticket history error:', error);
      throw error;
    }
  },

  // Generate AI summary for ticket history
  generateTicketSummary: async (ticketId) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/ai-summary`);
      return response.data;
    } catch (error) {
      console.error('Generate ticket AI summary error:', error);
      throw error;
    }
  },

  // #322: `offlineFaehig` schaltet die Warteschlange zu. Bewusst NICHT der
  // Standard -- Kommentare, die als Nebenwirkung eines anderen Vorgangs
  // entstehen (Abschluss, Uebergabe), sollen mit diesem Vorgang scheitern und
  // nicht allein nachgesendet werden.
  addCommentToTicket: async (ticketId, content, isInternal = false, { offlineFaehig = false } = {}) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/comments`, {
        content,
        isInternal
      });
      return response.data;
    } catch (error) {
      if (offlineFaehig && istNetzfehler(error)) {
        const eintrag = reiheKommentarEin({ ticketId, inhalt: content, isInternal });
        if (eintrag) return { eingereiht: true, eintrag };
      }
      console.error('Add comment to ticket error:', error);
      throw error;
    }
  },

  // #322: Sendet die Warteschlange ab. Vor jedem Nachsenden wird geprueft, ob
  // der Kommentar schon am Ticket haengt -- eine Verbindung, die nach dem
  // Schreiben abreisst, sieht von aussen aus wie eine, die nie zustande kam.
  sendeOfflineWarteschlange: async () => sendeWarteschlange(
    (eintrag) => axios.post(`/api/tickets/${eintrag.ticketId}/comments`, {
      content: eintrag.inhalt,
      isInternal: eintrag.isInternal
    }),
    async (eintrag) => {
      try {
        const resp = await axios.get(`/api/tickets/${eintrag.ticketId}/comments`, { disableSWR: true });
        return istDublette(eintrag, resp?.data?.comments || resp?.data || []);
      } catch (_) {
        // Laesst sich das nicht klaeren, wird normal gesendet.
        return false;
      }
    }
  ),

  updateTicketComment: async (ticketId, commentId, content) => {
    try {
      const response = await axios.patch(`/api/tickets/${ticketId}/comments/${commentId}`, { content });
      return response.data;
    } catch (error) {
      console.error('Update ticket comment error:', error);
      throw error;
    }
  },

  deleteTicketComment: async (ticketId, commentId) => {
    try {
      const response = await axios.delete(`/api/tickets/${ticketId}/comments/${commentId}`);
      return response.data;
    } catch (error) {
      console.error('Delete ticket comment error:', error);
      throw error;
    }
  },

  markTicketAsViewed: async (ticketId) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/viewed`);
      return response.data;
    } catch (error) {
      console.error('Mark ticket as viewed error:', error);
      throw error;
    }
  },

  // ================ TICKET ATTACHMENT METHODS ================

  // Upload files to ticket
  uploadTicketAttachments: async (ticketId, files, description = '') => {
    try {
      const formData = new FormData();
      
      // Add all files
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      // Add description if provided
      if (description) {
        formData.append('description', description);
      }

      console.log(`📎 [API SERVICE] Uploading ${files.length} files to ticket ${ticketId}`);

      const response = await axios.post(`/api/tickets/${ticketId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log(`📎 [API SERVICE] Successfully uploaded ${response.data.count} attachments`);
      return response.data;
    } catch (error) {
      console.error('Upload ticket attachments error:', error);
      throw error;
    }
  },

  // Parse an email file (e.g. .msg) to extract subject/body for prefill
  parseEmailFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/utils/parse-email-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Parse email file error:', error);
      throw error;
    }
  },

  // Download attachment content
  // #508: Audio-Anhang (Mail oder Ticket) transkribieren -> { success, comment }
  transkribiereAnhang: async (ticketId, { quelle, anhangId, emailId }) => {
    const response = await axios.post(`/api/tickets/${encodeURIComponent(ticketId)}/transkription`, { quelle, anhangId, ...(emailId ? { emailId } : {}) }, { timeout: 180000 });
    return response.data;
  },

  downloadTicketAttachment: async (ticketId, attachmentId) => {
    try {
      console.log(`📎 [API SERVICE] Downloading attachment ${attachmentId} from ticket ${ticketId}`);
      
      const response = await axios.get(`/api/tickets/${ticketId}/attachments/${attachmentId}/content`, {
        responseType: 'blob'
      });
      
      console.log(`📎 [API SERVICE] Downloaded attachment (${response.data.size} bytes)`);
      return response.data;
    } catch (error) {
      console.error('Download ticket attachment error:', error);
      throw error;
    }
  },

  // Get thumbnail for image attachments
  getTicketAttachmentThumbnail: async (ticketId, attachmentId) => {
    try {
      console.log(`📎 [API SERVICE DEBUG] Getting thumbnail for attachment ${attachmentId} from ticket ${ticketId}`);
      console.log(`📎 [API SERVICE DEBUG] Request URL: /api/tickets/${ticketId}/attachments/${attachmentId}/thumbnail`);
      
      const response = await axios.get(`/api/tickets/${ticketId}/attachments/${attachmentId}/thumbnail`, {
        responseType: 'blob'
      });
      
      console.log(`📎 [API SERVICE DEBUG] Retrieved thumbnail response:`, {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length'],
        blobSize: response.data.size,
        blobType: response.data.type
      });
      
      if (response.data.size === 0) {
        console.error(`📎 [API SERVICE DEBUG] Received empty blob for thumbnail ${attachmentId}`);
        throw new Error('Received empty thumbnail');
      }
      
      return response.data;
    } catch (error) {
      console.error(`📎 [API SERVICE DEBUG] Get thumbnail error for attachment ${attachmentId}:`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        responseData: error.response?.data
      });
      throw error;
    }
  },

  // Delete attachment
  deleteTicketAttachment: async (ticketId, attachmentId) => {
    try {
      console.log(`📎 [API SERVICE] Deleting attachment ${attachmentId} from ticket ${ticketId}`);
      
      const response = await axios.delete(`/api/tickets/${ticketId}/attachments/${attachmentId}`);
      
      console.log(`📎 [API SERVICE] Successfully deleted attachment`);
      return response.data;
    } catch (error) {
      console.error('Delete ticket attachment error:', error);
      throw error;
    }
  },

  // Teams data
  getTeamsMessages: async (limit = 30, unreadOnly = false) => {
    try {
      console.log(`💬 [API SERVICE] Getting Teams messages (limit: ${limit}, unread only: ${unreadOnly})...`);
      
      const response = await axios.get('/api/teams/messages', {
        params: { 
          limit,
          unreadOnly: unreadOnly ? 'true' : 'false'
        }
      });
      
      console.log('💬 [API SERVICE] Teams messages response:', {
        count: response.data.value?.length || 0,
        totalCount: response.data.totalCount,
        live: response.data.live,
        cached: response.data.cached,
        unreadOnly: unreadOnly
      });
      
      return response.data;
    } catch (error) {
      console.error('💬 [API SERVICE] Get Teams messages error:', error);
      console.error('💬 [API SERVICE] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Teams to Ticket functionality
  addTeamsMessageToTicket: async (ticketId, messageId, messageData = null) => {
    try {
      console.log(`💬 [API SERVICE] Adding Teams message ${messageId} to ticket ${ticketId} (with cache data: ${!!messageData})`);
      
      const response = await axios.post(`/api/tickets/${ticketId}/teams-messages`, {
        messageId,
        messageData // Cache Teams-Message-Daten wenn vorhanden
      });
      
      console.log(`💬 [API SERVICE] Teams message added to ticket (cached: ${response.data.cached})`);
      
      return response.data;
    } catch (error) {
      console.error('Add Teams message to ticket error:', error);
      throw error;
    }
  },

  // Get linked email and Teams message IDs (optional date scoping + timeout)
  getLinkedItems: async (options = {}) => {
    try {
      const { startDate = null, endDate = null, timeoutMs = 6000, scope = null } = options || {};
      const params = new URLSearchParams();
      if (startDate instanceof Date) params.append('startDate', startDate.toISOString());
      if (endDate instanceof Date) params.append('endDate', endDate.toISOString());
      if (scope) params.append('scope', scope);
      const url = `/api/tickets/linked-items${params.toString() ? '?' + params.toString() : ''}`;
      try {
        const response = await axios.get(url, { timeout: timeoutMs });
        return response.data;
      } catch (firstErr) {
        // Gentle one-time retry on timeout with a slightly higher timeout
        if (firstErr?.code === 'ECONNABORTED') {
          try {
            const retryTimeout = Math.min((timeoutMs || 6000) + 6000, 20000);
            const response2 = await axios.get(url, { timeout: retryTimeout });
            return response2.data;
          } catch (retryErr) {
            throw retryErr;
          }
        }
        throw firstErr;
      }
    } catch (error) {
      console.error('Get linked items error:', error);
      throw error;
    }
  },

  // Get ticket by calendar event ID
  getTicketByCalendarEvent: async (calendarEventId) => {
    try {
      console.log(`📅 [API SERVICE] Getting ticket for calendar event ${calendarEventId}`);
      const response = await axios.get(`/api/tickets/by-calendar-event/${calendarEventId}`);
      console.log(`📅 [API SERVICE] Found ticket for calendar event:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Get ticket by calendar event error:', error);
      throw error;
    }
  },

  // Synchronize ticket calendar planning
  syncTicketCalendar: async (ticketId = null) => {
    try {
      console.log(`📅 [API SERVICE] Synchronizing ticket calendar${ticketId ? ' for ticket ' + ticketId : ' (all tickets)'}`);
      
      const response = await axios.post('/api/tickets/sync-calendar', {
        ticketId
      });
      
      console.log(`📅 [API SERVICE] Calendar synchronization completed:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Sync ticket calendar error:', error);
      throw error;
    }
  },

  // Delete a calendar event from current user's calendar
  deleteCalendarEvent: async (eventId) => {
    try {
      const encodedId = encodeURIComponent(eventId);
      const response = await axios.delete(`/api/events/${encodedId}`);
      return response.data;
    } catch (error) {
      console.error('Delete calendar event error:', error);
      throw error;
    }
  },

  // Delete a calendar event from a specific user's calendar
  deleteCalendarEventForUser: async (userEmail, eventId) => {
    try {
      const encodedEmail = encodeURIComponent(userEmail);
      const encodedId = encodeURIComponent(eventId);
      const response = await axios.delete(`/api/users/${encodedEmail}/events/${encodedId}`);
      return response.data;
    } catch (error) {
      console.error('Delete calendar event for user error:', error);
      throw error;
    }
  },

  // Repair orphaned ticket planning
  repairOrphanedPlanning: async () => {
    try {
      console.log(`🔧 [API SERVICE] Starting orphaned ticket planning repair...`);
      
      const response = await axios.post('/api/tickets/repair-orphaned-planning');
      
      console.log(`🔧 [API SERVICE] Repair completed:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Repair orphaned planning error:', error);
      throw error;
    }
  },

  createTicketFromTeamsMessage: async (messageId, ticketData, messageData = null) => {
    try {
      console.log(`💬 [API SERVICE] Creating ticket from Teams message ${messageId} (with cache data: ${!!messageData})`);
      
      const response = await axios.post('/api/tickets/from-teams-message', {
        messageId,
        messageData, // Cache Teams-Message-Daten wenn vorhanden
        ticketData // Ticket-Daten als separates Objekt
      });
      
      console.log(`💬 [API SERVICE] Ticket created from Teams message (cached: ${response.data.cached})`);
      
      return response.data;
    } catch (error) {
      console.error('Create ticket from Teams message error:', error);
      throw error;
    }
  },

  // Admin functions
  getUsers: async () => {
    try {
      const response = await axios.get('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  },

  // Benutzerliste für die Manager-Ansichten (Alle Kalender, Vorgänge,
  // Projekt-Wizard). Bewusst NICHT /api/admin/users: die Route ist seit #44
  // isAdmin-only — Manager ohne Admin-Flag bekamen dort 403 und die
  // Ansichten liefen in den Fehler-Banner (#185).
  getCalendarUsers: async () => {
    try {
      const response = await axios.get('/api/users');
      return response.data;
    } catch (error) {
      console.error('Get calendar users error:', error);
      throw error;
    }
  },

  updateUserRole: async (userId, roleData) => {
    try {
      const response = await axios.patch(`/api/admin/users/${userId}/role`, roleData);
      return response.data;
    } catch (error) {
      console.error('Update user role error:', error);
      throw error;
    }
  },

  // ===== Kundenstamm (#35) =====
  getCustomers: async () => {
    try {
      // Cache-Buster wie bei getReleaseNote: ohne ihn darf der Browser die
      // Antwort heuristisch cachen — neu angelegte Kunden erscheinen dann
      // erst nach Reload im Auswahlmenü (#110).
      const response = await axios.get('/api/customers?_=' + Date.now());
      return response.data;
    } catch (error) {
      console.error('Get customers error:', error);
      throw error;
    }
  },

  getCustomersAdmin: async () => {
    try {
      const response = await axios.get('/api/admin/customers');
      return response.data;
    } catch (error) {
      console.error('Get customers (admin) error:', error);
      throw error;
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await axios.post('/api/admin/customers', customerData);
      return response.data;
    } catch (error) {
      console.error('Create customer error:', error);
      throw error;
    }
  },

  updateCustomer: async (id, customerData) => {
    try {
      const response = await axios.patch(`/api/admin/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Update customer error:', error);
      throw error;
    }
  },

  // #204: hartes Löschen nur ohne Referenzen — 409 trägt refs-Zähler
  deleteCustomer: async (id) => {
    try {
      const response = await axios.delete(`/api/admin/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete customer error:', error);
      throw error;
    }
  },

  // ===== Web Push (#16 B6, W6) =====
  getVapidPublicKey: async () => {
    try {
      const response = await axios.get('/api/push/vapid-public-key');
      return response.data;
    } catch (error) {
      console.error('Get VAPID public key error:', error);
      throw error;
    }
  },

  savePushSubscription: async (subscription) => {
    try {
      const response = await axios.post('/api/push/subscription', { subscription });
      return response.data;
    } catch (error) {
      console.error('Save push subscription error:', error);
      throw error;
    }
  },

  deletePushSubscription: async (endpoint) => {
    try {
      const response = await axios.delete('/api/push/subscription', { data: { endpoint } });
      return response.data;
    } catch (error) {
      console.error('Delete push subscription error:', error);
      throw error;
    }
  },

  // ===== Ansprechpartner (#105) =====
  getCustomerContacts: async (customerId) => {
    try {
      // Cache-Buster wie bei getCustomers (#110)
      const response = await axios.get(`/api/customers/${customerId}/contacts`, { params: { _: Date.now() } });
      return response.data;
    } catch (error) {
      console.error('Get customer contacts error:', error);
      throw error;
    }
  },

  createCustomerContact: async (customerId, contactData) => {
    try {
      const response = await axios.post(`/api/admin/customers/${customerId}/contacts`, contactData);
      return response.data;
    } catch (error) {
      console.error('Create customer contact error:', error);
      throw error;
    }
  },

  updateCustomerContact: async (contactId, contactData) => {
    try {
      const response = await axios.patch(`/api/admin/customer-contacts/${contactId}`, contactData);
      return response.data;
    } catch (error) {
      console.error('Update customer contact error:', error);
      throw error;
    }
  },

  // #204: hartes Löschen nur ohne Ticket-Referenzen — 409 trägt refs-Zähler
  deleteCustomerContact: async (contactId) => {
    try {
      const response = await axios.delete(`/api/admin/customer-contacts/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Delete customer contact error:', error);
      throw error;
    }
  },

  // ===== API-Keys (#12) =====
  // Feedback (#42)
  // #575: screenshotFiles = bis zu fünf Bilder, alle im Feld `screenshot`.
  submitFeedback: async ({ type, message, contextUrl, contextMeta, screenshotFile, screenshotFiles }) => {
    try {
      const form = new FormData();
      form.append('type', type || 'bug');
      form.append('message', message || '');
      if (contextUrl) form.append('contextUrl', contextUrl);
      if (contextMeta) form.append('contextMeta', JSON.stringify(contextMeta));
      const bilder = screenshotFiles || (screenshotFile ? [screenshotFile] : []);
      bilder.forEach((datei) => form.append('screenshot', datei));
      const response = await axios.post('/api/feedback', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    } catch (error) {
      console.error('Submit feedback error:', error);
      throw error;
    }
  },

  getFeedbackAdmin: async (status) => {
    try {
      // Cache-Buster wie bei getCustomers/getReleaseNote: sonst kann der
      // Browser die Liste heuristisch cachen und Triage-Änderungen erscheinen
      // erst nach Seiten-Reload (#124).
      const params = status ? { status, _: Date.now() } : { _: Date.now() };
      const response = await axios.get('/api/admin/feedback', { params });
      return response.data;
    } catch (error) {
      console.error('Get feedback error:', error);
      throw error;
    }
  },

  updateFeedbackAdmin: async (id, updates) => {
    try {
      const response = await axios.patch(`/api/admin/feedback/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update feedback error:', error);
      throw error;
    }
  },

  // #83: Eintrag endgültig löschen
  deleteFeedbackAdmin: async (id) => {
    try {
      const response = await axios.delete(`/api/admin/feedback/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete feedback error:', error);
      throw error;
    }
  },

  getApiKeys: async () => {
    try {
      const response = await axios.get('/api/admin/api-keys');
      return response.data;
    } catch (error) {
      console.error('Get api keys error:', error);
      throw error;
    }
  },

  createApiKey: async ({ name, lifetimeDays, userId }) => {
    try {
      const response = await axios.post('/api/admin/api-keys', { name, lifetimeDays, userId });
      return response.data;
    } catch (error) {
      console.error('Create api key error:', error);
      throw error;
    }
  },

  revokeApiKey: async (id) => {
    try {
      const response = await axios.post(`/api/admin/api-keys/${id}/revoke`);
      return response.data;
    } catch (error) {
      console.error('Revoke api key error:', error);
      throw error;
    }
  },

  // Persönliche API-Keys (#80): Self-Service, agieren mit den Rechten des
  // angemeldeten Benutzers. Cache-Buster wie bei den Admin-Listen (#110/#124).
  getMyApiKeys: async () => {
    try {
      const response = await axios.get('/api/user/api-keys', { params: { _: Date.now() } });
      return response.data;
    } catch (error) {
      console.error('Get personal api keys error:', error);
      throw error;
    }
  },

  createMyApiKey: async ({ name, lifetimeDays }) => {
    try {
      const response = await axios.post('/api/user/api-keys', { name, lifetimeDays });
      return response.data;
    } catch (error) {
      console.error('Create personal api key error:', error);
      throw error;
    }
  },

  revokeMyApiKey: async (id) => {
    try {
      const response = await axios.post(`/api/user/api-keys/${id}/revoke`);
      return response.data;
    } catch (error) {
      console.error('Revoke personal api key error:', error);
      throw error;
    }
  },

  // #627: Admin-Route statt /api/setup/status (die liefert die Liste seit #46 nicht mehr)
  getAllowedEmails: async () => {
    try {
      const response = await axios.get('/api/admin/settings/allowed-emails');
      return response.data;
    } catch (error) {
      console.error('Get allowed emails error:', error);
      throw error;
    }
  },

  updateAllowedEmails: async (allowedEmails) => {
    try {
      const response = await axios.patch('/api/admin/settings/allowed-emails', { allowedEmails });
      return response.data;
    } catch (error) {
      console.error('Update allowed emails error:', error);
      throw error;
    }
  },

  // Email filter settings
  getEmailFilterSettings: async () => {
    try {
      const response = await axios.get('/api/user/email-filter-settings');
      return response.data;
    } catch (error) {
      console.error('Get email filter settings error:', error);
      throw error;
    }
  },

  saveEmailFilterSettings: async (filterSettings, keywords) => {
    try {
      const response = await axios.post('/api/user/email-filter-settings', {
        filterSettings,
        keywords
      });
      return response.data;
    } catch (error) {
      console.error('Save email filter settings error:', error);
      throw error;
    }
  },

  // Validate delegated rights to an additional mailbox
  validateMailbox: async (mailbox) => {
    try {
      const response = await axios.get('/api/user/validate-mailbox', { params: { mailbox } });
      return response.data;
    } catch (error) {
      console.error('Validate mailbox error:', error);
      return { success: false, error: error.message };
    }
  },

  // Teams filter settings
  getTeamsFilterSettings: async () => {
    try {
      const response = await axios.get('/api/user/teams-filter-settings');
      return response.data;
    } catch (error) {
      console.error('Get teams filter settings error:', error);
      throw error;
    }
  },

  saveTeamsFilterSettings: async (filterSettings, keywords) => {
    try {
      const response = await axios.post('/api/user/teams-filter-settings', {
        filterSettings,
        keywords
      });
      return response.data;
    } catch (error) {
      console.error('Save teams filter settings error:', error);
      throw error;
    }
  },

  // Ticket filter settings
  getTicketFilterSettings: async () => {
    try {
      const response = await axios.get('/api/user/ticket-filter-settings');
      return response.data;
    } catch (error) {
      console.error('Get ticket filter settings error:', error);
      throw error;
    }
  },

  getTicketFilterSettingsByEmail: async (email) => {
    try {
      const response = await axios.get('/api/user/ticket-filter-settings/by-email', { params: { email } });
      return response.data;
    } catch (error) {
      console.error('Get ticket filter settings by email error:', error);
      throw error;
    }
  },

  saveTicketFilterSettings: async (filterSettings, keywords) => {
    try {
      const response = await axios.post('/api/user/ticket-filter-settings', {
        filterSettings,
        keywords
      });
      return response.data;
    } catch (error) {
      console.error('Save ticket filter settings error:', error);
      throw error;
    }
  },

  // #564: Persönliche Einstellungen je Benutzer (Darstellung, Sprache, Reiter, Dashboard).
  // Lesen mit Buster: der Stand lebt in services/benutzerEinstellungen, nicht im SWR-Cache.
  getUserSettings: async () => {
    const response = await axios.get('/api/user/settings', { params: { _: Date.now() } });
    return response.data;
  },

  // Nur die Schlüssel, die sich ändern; der Server führt zusammen.
  patchUserSettings: async (aenderung) => {
    const response = await axios.patch('/api/user/settings', aenderung);
    return response.data;
  },

  // Calendar marker configuration (public)
  getCalendarMarkers: async () => {
    try {
      const response = await axios.get('/api/calendar-markers');
      return response.data;
    } catch (error) {
      console.error('Get calendar markers error:', error);
      throw error;
    }
  },

  // Calendar marker configuration (admin)
  getCalendarMarkersAdmin: async () => {
    try {
      const response = await axios.get('/api/admin/calendar-markers');
      return response.data;
    } catch (error) {
      console.error('Get calendar markers admin error:', error);
      throw error;
    }
  },

  // AI Integration (admin)
  getAiIntegrationConfig: async () => {
    try {
      // Disable any client-side SWR for admin config; always fetch fresh with cache-buster
      const response = await axios.get(`/api/admin/ai-integration?_=${Date.now()}`, { disableSWR: true, swrTTL: 0 });
      return response.data;
    } catch (error) {
      console.error('Get AI integration config error:', error);
      throw error;
    }
  },

  updateAiIntegrationConfig: async (data) => {
    try {
      const response = await axios.patch('/api/admin/ai-integration', data);
      return response.data;
    } catch (error) {
      console.error('Update AI integration config error:', error);
      throw error;
    }
  },

  // Embeddings (#14): Provider-Test, Index-Status, Backfill
  testEmbedding: async () => {
    try {
      const response = await axios.post('/api/admin/ai-integration/test-embedding', {});
      return response.data;
    } catch (error) {
      console.error('Test embedding error:', error);
      throw error;
    }
  },

  getEmbeddingsStatus: async () => {
    try {
      const response = await axios.get('/api/admin/embeddings/status', { params: { _: Date.now() } });
      return response.data;
    } catch (error) {
      console.error('Get embeddings status error:', error);
      throw error;
    }
  },

  startEmbeddingsBackfill: async () => {
    try {
      const response = await axios.post('/api/admin/embeddings/backfill', {});
      return response.data;
    } catch (error) {
      console.error('Start embeddings backfill error:', error);
      throw error;
    }
  },

  testAiIntegration: async (data) => {
    try {
      const response = await axios.post('/api/admin/ai-integration/test', data);
      return response.data;
    } catch (error) {
      console.error('Test AI integration error:', error);
      throw error;
    }
  },

  // Global notification config
  getNotificationConfig: async () => {
    try {
      const response = await axios.get(`/api/notification?_=${Date.now()}`);
      return response.data;
    } catch (error) {
      console.error('Get notification config error:', error);
      throw error;
    }
  },

  getNotificationConfigAdmin: async () => {
    try {
      const response = await axios.get(`/api/admin/notification?_=${Date.now()}`);
      return response.data;
    } catch (error) {
      console.error('Get admin notification config error:', error);
      throw error;
    }
  },

  // Admin Logs
  getSystemLogs: async ({ limit = 100, offset = 0, action = '', ticketNumber = '', q = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', String(limit));
      if (offset) params.append('offset', String(offset));
      if (action) params.append('action', action);
      if (ticketNumber) params.append('ticketNumber', ticketNumber);
      if (q) params.append('q', q);
      const response = await axios.get(`/api/admin/logs${params.toString() ? '?' + params.toString() : ''}`);
      return response.data;
    } catch (error) {
      console.error('Get system logs error:', error);
      throw error;
    }
  },

  updateNotificationConfig: async (data) => {
    try {
      const response = await axios.patch('/api/admin/notification', data);
      return response.data;
    } catch (error) {
      console.error('Update notification config error:', error);
      throw error;
    }
  },

  getReleaseNoteAdmin: async () => {
    try {
      const response = await axios.get('/api/admin/release-note?_=' + Date.now());
      return response.data;
    } catch (error) {
      console.error('Get release note admin error:', error);
      throw error;
    }
  },

  updateReleaseNoteAdmin: async (data) => {
    try {
      const response = await axios.patch('/api/admin/release-note', data);
      return response.data;
    } catch (error) {
      console.error('Update release note admin error:', error);
      throw error;
    }
  },

  getReleaseNote: async () => {
    try {
      const response = await axios.get('/api/release-note?_=' + Date.now());
      return response.data;
    } catch (error) {
      console.error('Get release note error:', error);
      throw error;
    }
  },

  ackReleaseNote: async () => {
    try {
      const response = await axios.post('/api/release-note/ack');
      return response.data;
    } catch (error) {
      console.error('Ack release note error:', error);
      throw error;
    }
  },

  // #85: Changelog auf Abruf (unabhängig vom Einmal-Overlay)
  getReleaseNoteHistory: async () => {
    try {
      const response = await axios.get('/api/release-note/history?_=' + Date.now());
      return response.data;
    } catch (error) {
      console.error('Get release note history error:', error);
      throw error;
    }
  },

  // #118 (W7): versionierte Releasenotes (Quelle CHANGELOG.md via Import-Skript)
  getReleaseNoteVersions: async () => {
    try {
      const response = await axios.get('/api/release-note/versions?_=' + Date.now());
      return response.data;
    } catch (error) {
      console.error('Get release note versions error:', error);
      throw error;
    }
  },

  getReleaseNoteVersion: async (version) => {
    try {
      const response = await axios.get(`/api/release-note/versions/${encodeURIComponent(version)}?_=` + Date.now());
      return response.data;
    } catch (error) {
      console.error('Get release note version error:', error);
      throw error;
    }
  },

  getTicketChecklist: async (ticketId) => {
    try {
      const response = await axios.get(`/api/tickets/${ticketId}/checklist`);
      return response.data;
    } catch (error) {
      console.error('Get ticket checklist error:', error);
      throw error;
    }
  },

  createTicketChecklistItem: async (ticketId, content) => {
    try {
      const response = await axios.post(`/api/tickets/${ticketId}/checklist`, { content });
      return response.data;
    } catch (error) {
      console.error('Create checklist item error:', error);
      throw error;
    }
  },

  updateTicketChecklistItem: async (ticketId, itemId, updates) => {
    try {
      const response = await axios.patch(`/api/tickets/${ticketId}/checklist/${itemId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update checklist item error:', error);
      throw error;
    }
  },

  deleteTicketChecklistItem: async (ticketId, itemId) => {
    try {
      const response = await axios.delete(`/api/tickets/${ticketId}/checklist/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Delete checklist item error:', error);
      throw error;
    }
  },

  reorderTicketChecklist: async (ticketId, orderedIds) => {
    try {
      const response = await axios.put(`/api/tickets/${ticketId}/checklist/reorder`, { orderedIds });
      return response.data;
    } catch (error) {
      console.error('Reorder checklist error:', error);
      throw error;
    }
  },

  createCalendarMarker: async (markerData) => {
    try {
      const response = await axios.post('/api/admin/calendar-markers', markerData);
      return response.data;
    } catch (error) {
      console.error('Create calendar marker error:', error);
      throw error;
    }
  },

  updateCalendarMarker: async (markerId, markerData) => {
    try {
      const response = await axios.patch(`/api/admin/calendar-markers/${markerId}`, markerData);
      return response.data;
    } catch (error) {
      console.error('Update calendar marker error:', error);
      throw error;
    }
  },

  deleteCalendarMarker: async (markerId) => {
    try {
      const response = await axios.delete(`/api/admin/calendar-markers/${markerId}`);
      return response.data;
    } catch (error) {
      console.error('Delete calendar marker error:', error);
      throw error;
    }
  }
};

const RESUME_PATH_KEY = 'lastVisitedPathAfterAuth';
const RESUME_PATH_APPLY_KEY = 'lastVisitedPathShouldApply';

const getCurrentLocationPath = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.location.pathname + window.location.search + window.location.hash;
  } catch (_) {
    return null;
  }
};

const markResumePathShouldApply = () => {
  try {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(RESUME_PATH_APPLY_KEY, '1');
  } catch (_) {}
};

const clearResumePathShouldApply = () => {
  try {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(RESUME_PATH_APPLY_KEY);
  } catch (_) {}
};

// Duplicate guard: define only if not yet defined in module scope
let captureResumePath;
if (typeof captureResumePath === 'undefined') {
  captureResumePath = (options = {}) => {
    try {
      if (typeof window === 'undefined') return;
      const { forceAllow = false } = options;
      const shouldApply = options.shouldApply === true;
      const allowAutoSSO = forceAllow || hasWasLoggedInCookie();
      if (!allowAutoSSO) {
        sessionStorage.removeItem(RESUME_PATH_KEY);
        if (shouldApply) {
          clearResumePathShouldApply();
        }
        return;
      }

      const path = getCurrentLocationPath();
      if (!path || path === '/' || path.startsWith('/auth/')) {
        return;
      }

      sessionStorage.setItem(RESUME_PATH_KEY, path);
      if (shouldApply) {
        markResumePathShouldApply();
      }
    } catch (_) {}
  };
}

// Duplicate guard for helpers
let prepareResumePathForRelogin;
if (typeof prepareResumePathForRelogin === 'undefined') {
  prepareResumePathForRelogin = () => {
    captureResumePath({ forceAllow: true, shouldApply: true });
  };
}

let consumeResumePath;
if (typeof consumeResumePath === 'undefined') {
  consumeResumePath = () => {
    try {
      if (typeof window === 'undefined') return null;
      const shouldApply = sessionStorage.getItem(RESUME_PATH_APPLY_KEY) === '1';
      if (!shouldApply) return null;
      const path = sessionStorage.getItem(RESUME_PATH_KEY);
      sessionStorage.removeItem(RESUME_PATH_KEY);
      clearResumePathShouldApply();
      if (!path) return null;
      return path;
    } catch (_) {
      return null;
    }
  };
}

// Add request interceptor for error handling with automatic token refresh
axios.interceptors.response.use(
  response => {
    // Reset consecutive error counter on successful response
    if (window._consecutiveServerErrors) {
      window._consecutiveServerErrors = 0;
    }
    // #229: Antwort stammt aus dem Offline-Cache des Service Workers (sw-cached-at) —
    // bisher lief das als stille 200 durch und das Dashboard zeigte alte Daten.
    try {
      const cachedAt = response?.headers?.['sw-cached-at'];
      if (cachedAt) {
        window.dispatchEvent(new CustomEvent('swDegradedResponse', { detail: { kind: 'stale', cachedAt, url: response?.config?.url || null } }));
      }
    } catch (_) {}
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // #229: Offline-Fallback des Service Workers (503 {error:'offline'}) — App informieren,
    // kein Token-Refresh, kein Redirect.
    if (error.response?.status === 503 && error.response?.data?.error === 'offline') {
      try {
        window.dispatchEvent(new CustomEvent('swDegradedResponse', { detail: { kind: 'offline', url: originalRequest?.url || null } }));
      } catch (_) {}
      return Promise.reject(error);
    }

    // Don't redirect on setup-related errors
    if (originalRequest?.url?.includes('/api/setup') || 
        originalRequest?.url?.includes('/api/health')) {
      return Promise.reject(error);
    }

    // Skip token refresh for refresh requests
    if (originalRequest?.skipTokenRefresh) {
      return Promise.reject(error);
    }
    
    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshingToken) {
        // If a refresh is already in progress, queue this request
        console.log('🔄 [AUTH] 401 detected, queuing request while refresh in progress...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      isRefreshingToken = true;

      try {
        console.log('🔄 [AUTH] 401 detected, attempting token refresh...');
        await apiService.refreshToken();
        
        console.log('✅ [AUTH] Token refresh successful, processing queued requests...');
        
        // Token refreshed successfully, process queued requests
        processQueue(null);
        isRefreshingToken = false;
        
        // Notify components that token refresh was successful
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('tokenRefreshSuccess'));
        }
        
        // Retry the original request
        console.log('🔄 [AUTH] Retrying original request after token refresh...');
        return axios(originalRequest);
      } catch (refreshError) {
        // Token refresh failed; ggf. manueller Login erforderlich
        console.error('❌ [AUTH] Token refresh failed:', refreshError.message);
        processQueue(refreshError);
        isRefreshingToken = false;
        
        // Automatic relogin for all authentication failures
        if (refreshError.message.includes('Session expired') || 
            refreshError.message.includes('login required') || 
            refreshError.message.includes('automatic relogin required')) {
          const allowAutoSSO = hasWasLoggedInCookie();
          if (allowAutoSSO) {
            console.warn('🔄 [AUTH] Authentication failed - performing automatic relogin (cookie present)');
            prepareResumePathForRelogin();
            // Markiere Auto-Relogin als aktiv
            try { sessionStorage.setItem('autoReloginInProgress', '1'); } catch (_) {}
            // Notify components about impending redirect
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('automaticReloginStarted'));
            }
            setTimeout(() => {
              window.location.href = '/auth/sso';
            }, 100);
          } else {
            console.warn('🔄 [AUTH] Authentication failed - manual login required (no cookie). Staying on welcome page.');
            try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('manualLoginRequired')); } catch (_) {}
            // Optional: gehe zur Startseite, um Willkommensbildschirm zu zeigen
            try {
              if (typeof window !== 'undefined') {
                const isHome = window.location.pathname === '/';
                if (!isHome) window.location.href = '/';
              }
            } catch (_) {}
          }
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Handle setup errors
    if (error.response?.status === 503 && error.response?.data?.setupRequired) {
      console.warn('Setup required - redirecting to setup');
      window.location.href = '/setup';
      return Promise.reject(error);
    }

    // Handle 500 errors with improved auth detection
    if (error.response?.status === 500) {
      console.warn('🚨 [AUTH] 500 Server error detected - analyzing for auth issues');
      
      // Get all available error information for debugging
      const errorData = error.response?.data;
      const errorMessage = errorData?.error || errorData?.details || error.message || '';
      const statusText = error.response?.statusText || '';
      
      console.log('🔍 [AUTH] 500 Error analysis:', {
        url: originalRequest?.url,
        errorMessage,
        statusText,
        errorData,
        hasRetry: originalRequest._retry
      });
      
      // Common auth-related error patterns
      const authErrorPatterns = [
        'token',
        'unauthorized', 
        'authentication',
        'invalid_grant',
        'expired',
        'access_denied',
        'insufficient_privileges',
        'forbidden',
        'invalid_client',
        'consent_required',
        'interaction_required'
      ];
      
      const isAuthRelated = authErrorPatterns.some(pattern => 
        errorMessage.toLowerCase().includes(pattern) || statusText.toLowerCase().includes(pattern)
      );
      
      console.log('🔍 [AUTH] Auth-related check:', {
        isAuthRelated,
        errorMessage,
        patterns: authErrorPatterns.filter(p => 
          errorMessage.toLowerCase().includes(p) || statusText.toLowerCase().includes(p)
        )
      });

      // For API endpoints with 500 errors, always try token refresh on first attempt
      // This is because 500 errors from Microsoft Graph are often auth-related
      const isApiCall = originalRequest?.url?.startsWith('/api/');
      const shouldTryRefresh = isApiCall && !originalRequest._retry;
      
      console.log('🔍 [AUTH] Should try token refresh?', {
        isApiCall,
        hasRetry: originalRequest._retry,
        shouldTryRefresh
      });

      if (shouldTryRefresh) {
        originalRequest._retry = true;
        
        try {
          console.log('🔄 [AUTH] 500 error on API call - attempting token refresh...');
          await apiService.refreshToken();
          
          console.log('✅ [AUTH] Token refresh successful, retrying original request...');
          
          // Notify components that token refresh was successful
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('tokenRefreshSuccess'));
          }
          
          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('❌ [AUTH] Token refresh failed for 500 error:', refreshError.message);
          
          // Automatic relogin only if cookie vorhanden; sonst auf Startseite bleiben
          if (refreshError.message.includes('Session expired') || 
              refreshError.message.includes('login required') || 
              refreshError.message.includes('automatic relogin required')) {
            const allowAutoSSO = hasWasLoggedInCookie();
            if (allowAutoSSO) {
              console.warn('🔄 [AUTH] Authentication failed - performing automatic relogin (cookie present)');
              prepareResumePathForRelogin();
              // Markiere Auto-Relogin als aktiv
              try { sessionStorage.setItem('autoReloginInProgress', '1'); } catch (_) {}
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('automaticReloginStarted'));
              }
              setTimeout(() => {
                window.location.href = '/auth/sso';
              }, 100);
            } else {
              console.warn('🔄 [AUTH] Authentication failed - manual login required (no cookie). Staying on welcome page.');
              try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('manualLoginRequired')); } catch (_) {}
              try {
                if (typeof window !== 'undefined') {
                  const isHome = window.location.pathname === '/';
                  if (!isHome) window.location.href = '/';
                }
              } catch (_) {}
            }
          }
          
          return Promise.reject(refreshError);
        }
      }
      
      // Track consecutive 500 errors for non-auth related issues
      if (!window._consecutiveServerErrors) {
        window._consecutiveServerErrors = 0;
      }
      
      // Only count API endpoint errors (not static resources)
      if (originalRequest?.url?.startsWith('/api/')) {
        window._consecutiveServerErrors++;
        
        console.warn(`Consecutive API 500 errors: ${window._consecutiveServerErrors}`);
        
        // If we have multiple consecutive API 500 errors
        if (window._consecutiveServerErrors >= 3) {
          console.warn('Multiple consecutive server errors detected');
          
          // Reset counter
          window._consecutiveServerErrors = 0;
          
          const allowAutoSSO = hasWasLoggedInCookie();
          let href = '/';
          if (allowAutoSSO) {
            prepareResumePathForRelogin();
            // Markiere Auto-Relogin als aktiv
            try { sessionStorage.setItem('autoReloginInProgress', '1'); } catch (_) {}
            href = '/auth/sso';
          }
          // Kehrwoche B (#236 / #31): statt blockierendem alert() ein CustomEvent — App.js zeigt
          // einen Action-Toast („Jetzt anmelden") und leitet erst danach (bzw. nach Ablauf) weiter.
          // Ohne Listener (App noch nicht gemountet) sofort weiterleiten wie bisher.
          if (typeof window !== 'undefined') {
            const handled = !window.dispatchEvent(new CustomEvent('sessionExpired', {
              cancelable: true,
              detail: { mode: allowAutoSSO ? 'sso' : 'manual', href }
            }));
            if (!handled && (href !== '/' || window.location.pathname !== '/')) {
              window.location.href = href;
            }
          }
          
          return Promise.reject(new Error('Session expired'));
        }
      }
    } else {
      // Reset consecutive error counter on non-500 errors
      if (window._consecutiveServerErrors) {
        window._consecutiveServerErrors = 0;
      }
    }
    
    return Promise.reject(error);
  }
);

const PATH_CAPTURE_EVENT = '__captureResumePath';

if (typeof window !== 'undefined') {
  try {
    const handlePathCaptureEvent = (event) => {
      try { captureResumePath(event?.detail || {}); } catch (_) {}
    };
    const handleBeforeUnload = () => {
      try { captureResumePath({ shouldApply: true }); } catch (_) {}
    };
    const handleAutoReloginStarted = () => {
      prepareResumePathForRelogin();
    };
    const handleTokenRefreshSuccess = () => {
      try {
        // Nur Resume anwenden, wenn es explizit für Auto-Relogin markiert wurde
        const isAutoRelogin = sessionStorage.getItem('autoReloginInProgress') === '1';
        if (!isAutoRelogin) return;
        
        // Flag sofort löschen, um Loops zu vermeiden
        sessionStorage.removeItem('autoReloginInProgress');
        
        const path = consumeResumePath();
        const current = getCurrentLocationPath();
        if (path && current !== path) {
          window.location.replace(path);
        }
      } catch (_) {}
    };

    window.addEventListener(PATH_CAPTURE_EVENT, handlePathCaptureEvent);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('automaticReloginStarted', handleAutoReloginStarted);
    window.addEventListener('tokenRefreshSuccess', handleTokenRefreshSuccess);

    try {
      const hot = typeof import.meta !== 'undefined' && import.meta && import.meta.hot;
      if (hot) {
        hot.dispose(() => {
          try {
            window.removeEventListener(PATH_CAPTURE_EVENT, handlePathCaptureEvent);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('automaticReloginStarted', handleAutoReloginStarted);
            window.removeEventListener('tokenRefreshSuccess', handleTokenRefreshSuccess);
          } catch (_) {}
        });
      }
    } catch (_) {}
  } catch (_) {}
}

export default apiService; 